import { useState } from 'react';
import { Button, Notification } from '@sorrem-client/ui/components';
import type { FallenChoice, PlayerFallenPayload } from '@sorrem/shared';
import { useSecondsUntil } from '@sorrem-client/ui/hooks';
import styles from './YouDiedOverlay.module.scss';

interface YouDiedOverlayProps {
    fallen: PlayerFallenPayload;
    onChoose: (choice: FallenChoice) => void;
}

/**
 * Shown while the local player has fallen in a Crucible: what the fall cost,
 * what re-manifesting where they fell would cost, and how long they have to
 * choose before the Crucible casts them out (see #94).
 */
export function YouDiedOverlay({ fallen, onChoose }: YouDiedOverlayProps): JSX.Element {
    const secondsLeft = useSecondsUntil(fallen.decisionDeadlineMs) ?? 0;
    // The fall a choice was made for. A fall sent again means the Crucible refused it (#72): choose again.
    const [chosenFor, setChosenFor] = useState<PlayerFallenPayload | null>(null);
    const hasChosen = chosenFor === fallen;

    const choose = (choice: FallenChoice): void => {
        setChosenFor(fallen);
        onChoose(choice);
    };

    return (
        <Notification title="You have fallen" message="The Veil won't let the Crucible keep you.">
            <div className={styles.fallen}>
                <p className={styles.price}>
                    {fallen.remanifestPrice > 0
                        ? `Re-manifest here for ${fallen.remanifestPrice} essence, from what you carry first, then your vessel.`
                        : 'Re-manifest here. This Crucible asks nothing for it.'}
                </p>
                {!fallen.canAffordRemanifest && (
                    <p className={styles.warning}>You don't have enough essence to re-manifest.</p>
                )}
                {fallen.isDropPenalized && (
                    <p className={styles.cost}>
                        Leaving, or letting the time run out, fails the run for you: you lose what you gathered here,
                        and the Crucible may take something you carried in.
                    </p>
                )}
                <p className={styles.countdown}>The Crucible casts you out in {secondsLeft}s.</p>
                <div className={styles.actions}>
                    <Button
                        onClick={() => choose('remanifest')}
                        disabled={hasChosen || !fallen.canAffordRemanifest}
                    >
                        Re-manifest
                    </Button>
                    <Button variant="secondary" onClick={() => choose('leave')} disabled={hasChosen}>
                        Leave the Crucible
                    </Button>
                </div>
            </div>
        </Notification>
    );
}
